import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { theme } from '@resistapp/client/components/shared/theme';
import { useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { useResetLabWorkbook } from '../../../hooks/api';
import type { LabWorkbookSummary } from '../api/use-lab-workbook';

type ResetLabWorkbookControlProps = {
  workbookId: string;
  workbookName?: string | null;
  disabled?: boolean;
  reasons?: string[];
  onReset?: (summary: LabWorkbookSummary) => void;
};

export function ResetLabWorkbookControl({
  workbookId,
  workbookName,
  disabled = false,
  reasons = [],
  onReset,
}: ResetLabWorkbookControlProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const queryClient = useQueryClient();
  const mutation = useResetLabWorkbook();
  const isSubmitting = mutation.isPending;
  const resetButtonStyles = {
    bg: theme.colors.red900,
    color: 'white',
    _hover: { bg: theme.colors.red800 },
    _active: { bg: theme.colors.red700 },
    _disabled: { bg: theme.colors.red900, color: 'white', opacity: 0.5, cursor: 'not-allowed' },
  } as const;

  const openDialog = () => {
    if (disabled) {
      return;
    }
    onOpen();
  };

  const runReset = async () => {
    try {
      const summary = await mutation.mutateAsync({ workbookId });
      await queryClient.invalidateQueries({ queryKey: ['labWorkbook', workbookId] });
      onReset?.(summary as LabWorkbookSummary);
      onClose();
      window.location.reload();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Reset failed. Please try again.';
      toast({
        title: 'Reset failed',
        description: message,
        status: 'error',
      });
    }
  };
  const handleConfirm = () => {
    void runReset();
  };

  const disabledReason = reasons.length > 0 ? reasons.join(' ') : 'Reset unavailable.';

  return (
    <>
      <Tooltip label={disabledReason} isDisabled={!disabled} hasArrow>
        <Button
          size="sm"
          {...resetButtonStyles}
          onClick={openDialog}
          isDisabled={disabled || isSubmitting}
          isLoading={isSubmitting}
          loadingText="Resetting"
        >
          Reset Workbook
        </Button>
      </Tooltip>

      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Reset workbook
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb={3}>
                This will wipe workbook progress. Sample rows and gene selections will be preserved and become editable.
              </Text>
              <Text>Continue resetting{workbookName ? ` “${workbookName}”` : ''}?</Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} mr={3}>
                Cancel
              </Button>
              <Button onClick={handleConfirm} isLoading={isSubmitting} {...resetButtonStyles}>
                Reset
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
